import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { FiPlus, FiEdit, FiTrash2, FiRefreshCw, FiUser } from 'react-icons/fi';
import toast from 'react-hot-toast';

const CATEGORIAS = [
  { value: 'administrador', label: 'Administrador', desc: 'Todas as permissões' },
  { value: 'suporte', label: 'Suporte', desc: 'Acesso total ao sistema' },
  { value: 'financeiro', label: 'Financeiro', desc: 'Dashboard, pagamentos, contratos' },
  { value: 'condutor', label: 'Condutor', desc: 'Dashboard, lista de passageiros' }
];

const ROLES_PERMITIDOS = ['administrador', 'suporte', 'admin'];

const GerenciarUsuarios = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [usuarioEdit, setUsuarioEdit] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    senha: '',
    tipo: 'condutor'
  });

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/api/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (usuario = null) => {
    if (usuario) {
      setUsuarioEdit(usuario);
      setFormData({
        nome: usuario.nome,
        email: usuario.email,
        telefone: usuario.telefone || '',
        senha: '',
        tipo: usuario.tipo
      });
    } else {
      setUsuarioEdit(null);
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        senha: '',
        tipo: 'condutor'
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setUsuarioEdit(null);
  };

  const formatarTelefone = (valor) => {
    const v = valor.replace(/\D/g, '');
    if (v.length <= 10) {
      return v.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return v.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome?.trim() || !formData.email?.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    if (!usuarioEdit && !formData.senha) {
      toast.error('Senha é obrigatória para novo usuário');
      return;
    }
    if (formData.senha && formData.senha.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      if (usuarioEdit) {
        await api.put(`/api/usuarios/${usuarioEdit.id}`, formData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await api.post('/api/usuarios', formData);
        toast.success('Usuário criado com sucesso');
      }
      handleCloseModal();
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  const handleExcluir = async (usuario) => {
    if (usuario.id === user?.id) {
      toast.error('Você não pode excluir seu próprio usuário');
      return;
    }
    if (!window.confirm(`Excluir o usuário ${usuario.nome}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await api.delete(`/api/usuarios/${usuario.id}`);
      toast.success('Usuário excluído com sucesso');
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const getCategoriaLabel = (tipo) => {
    return CATEGORIAS.find(c => c.value === tipo)?.label || tipo;
  };

  const podeGerenciar = ROLES_PERMITIDOS.includes(user?.tipo);

  if (!podeGerenciar) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h2 className="text-2xl font-bold text-white mb-2">Acesso Negado</h2>
        <p className="text-gray-400">Você não tem permissão para gerenciar usuários.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Usuários do Sistema</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchUsuarios}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            <FiRefreshCw size={18} />
            Atualizar
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
          >
            <FiPlus size={18} />
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Telefone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Categoria</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  Nenhum usuário cadastrado
                </td>
              </tr>
            ) : (
              usuarios.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-3">
                        <FiUser className="text-sky-600" size={20} />
                      </div>
                      <span className="font-medium text-gray-900">{u.nome}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{u.telefone || '-'}</td>
                  <td className="px-6 py-4 text-gray-600">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-sky-100 text-sky-800">
                      {getCategoriaLabel(u.tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleOpenModal(u)}
                      className="text-sky-600 hover:text-sky-800 mr-3"
                      title="Editar"
                    >
                      <FiEdit size={20} />
                    </button>
                    {u.id !== user?.id && (
                      <button
                        onClick={() => handleExcluir(u)}
                        className="text-red-600 hover:text-red-800"
                        title="Excluir"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {usuarioEdit ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="Nome completo"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => {
                    const formatted = formatarTelefone(e.target.value);
                    setFormData({ ...formData, telefone: formatted });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="(00) 00000-0000"
                  maxLength="15"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="email@exemplo.com"
                  required
                  disabled={!!usuarioEdit}
                />
                {usuarioEdit && (
                  <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha {usuarioEdit ? '(deixe em branco para manter)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  placeholder="Mínimo 6 caracteres"
                  minLength={usuarioEdit ? 0 : 6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  required
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label} - {c.desc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  {usuarioEdit ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarUsuarios;
